import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Spin, Select, Button, Space, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import './styles.css';
import { 
  getYingshiLiveAddress, 
  getYingshiCamerasByDeviceSerial
} from '../../services/api';

// Define stream quality options
const STREAM_QUALITIES = [
  { label: '高清', value: 'hd' },
  { label: '标清', value: 'sd' },
];

// Define the channel interface
interface Channel {
  channelNo: number;
  channelName?: string;
  deviceSerial: string;
  isOnline?: boolean;
}

// Define the stream data interface
interface StreamData {
  hls?: string;
  hlsHd?: string;
  flvAddress?: string;
  hdFlvAddress?: string;
  rtmp?: string;
  rtmpHd?: string;
  status?: number;
  ret?: string;
}

// Define API response interfaces for local use
interface ApiResponse<T> {
  code: number;
  msg: string;
  data?: T;
}

interface StreamResponse {
  data: StreamData[];
}

interface VideoPlayerProps {
  // You can pass a pre-fetched URL or let the component fetch it
  url?: string;
  // Or pass device information for the component to fetch the URL
  appKey?: string;
  deviceSerial?: string;
  channelNo?: number;
  // Optional channels array for multi-channel support
  channels?: Channel[];
  // Optional callback when channels change
  onChannelChange?: (channel: Channel) => void;
  // Optional styling
  width?: string | number;
  height?: string | number;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({ 
  url: initialUrl, 
  appKey,
  deviceSerial,
  channelNo = 1,
  channels = [],
  onChannelChange,
  width = '100%', 
  height = '400px' 
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [url, setUrl] = useState<string | null>(initialUrl || null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [streamData, setStreamData] = useState<StreamData | null>(null);
  const [selectedQuality, setSelectedQuality] = useState<'hd' | 'sd'>('hd');
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [availableChannels, setAvailableChannels] = useState<Channel[]>(channels);

  // Function to fetch video stream URL from API
  const fetchStreamUrl = async (appKey: string, deviceSerial: string, channelNo: number = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getYingshiLiveAddress(appKey, deviceSerial, channelNo);
      
      if (response.data.code === 0 && response.data.data) {
        // Handle the nested data structure from the API
        const streamResponse = response.data.data as unknown as StreamResponse;
        
        if (streamResponse && streamResponse.data && streamResponse.data.length > 0) {
          const data = streamResponse.data[0];
          setStreamData(data);
          
          // Select the appropriate URL based on quality
          const streamUrl = selectedQuality === 'hd' 
            ? (data.hlsHd || data.hls || data.hdFlvAddress || data.flvAddress || data.rtmpHd || data.rtmp)
            : (data.hls || data.flvAddress || data.rtmp || data.hlsHd || data.hdFlvAddress || data.rtmpHd);
          
          if (streamUrl) {
            setUrl(streamUrl);
          } else {
            setError('未找到可用的视频流地址');
          }
        } else {
          setError('未找到可用的视频流');
        }
      } else {
        setError(response.data.msg || '获取视频流地址失败');
      }
    } catch (error) {
      console.error('获取视频流地址错误:', error);
      setError('获取视频流地址失败，请检查网络连接');
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch channels if not provided
  const fetchChannels = async (appKey: string, deviceSerial: string) => {
    if (channels.length > 0) {
      setAvailableChannels(channels);
      return;
    }
    
    try {
      setLoading(true);
      const response = await getYingshiCamerasByDeviceSerial(deviceSerial, appKey);
      
      if (response.data.code === 0 && response.data.data) {
        setAvailableChannels(response.data.data);
        
        // Select the first channel if available
        if (response.data.data.length > 0) {
          setSelectedChannel(response.data.data[0]);
        }
      }
    } catch (error) {
      console.error('获取通道列表错误:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initialize player when component mounts
  useEffect(() => {
    // If initial URL is provided, use it
    if (initialUrl) {
      setUrl(initialUrl);
    } 
    // Otherwise, fetch URL if device info is provided
    else if (appKey && deviceSerial) {
      fetchStreamUrl(appKey, deviceSerial, channelNo);
      fetchChannels(appKey, deviceSerial);
      
      // Set the initial selected channel
      if (channels.length > 0) {
        const initialChannel = channels.find(c => c.channelNo === channelNo) || channels[0];
        setSelectedChannel(initialChannel);
      }
    }
  }, [initialUrl, appKey, deviceSerial, channelNo]);

  // Handle quality change
  const handleQualityChange = (quality: 'hd' | 'sd') => {
    setSelectedQuality(quality);
    
    if (streamData) {
      // Select the appropriate URL based on new quality
      const newUrl = quality === 'hd' 
        ? (streamData.hlsHd || streamData.hls || streamData.hdFlvAddress || streamData.flvAddress || streamData.rtmpHd || streamData.rtmp)
        : (streamData.hls || streamData.flvAddress || streamData.rtmp || streamData.hlsHd || streamData.hdFlvAddress || streamData.rtmpHd);
      
      if (newUrl) {
        setUrl(newUrl);
      }
    } else if (appKey && deviceSerial && selectedChannel) {
      // Re-fetch the stream with the new quality preference
      fetchStreamUrl(appKey, deviceSerial, selectedChannel.channelNo);
    }
  };

  // Handle channel change
  const handleChannelChange = (channel: Channel) => {
    setSelectedChannel(channel);
    
    if (appKey && deviceSerial) {
      fetchStreamUrl(appKey, deviceSerial, channel.channelNo);
    }
    
    // Call the callback if provided
    if (onChannelChange) {
      onChannelChange(channel);
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    if (appKey && deviceSerial && selectedChannel) {
      fetchStreamUrl(appKey, deviceSerial, selectedChannel.channelNo);
    } else if (initialUrl) {
      // Just reinitialize the player with the same URL
      const currentUrl = url;
      setUrl(null);
      setTimeout(() => setUrl(currentUrl), 100);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !url) return;

    // Clean up function to destroy HLS instance
    let hls: Hls | null = null;

    // Function to initialize the player
    const initPlayer = () => {
      if (!video) return;
      
      // Clear any existing source
      if (video.src) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }

      // Check if the URL is an HLS (m3u8) stream
      if (url.includes('.m3u8')) {
        // Check if HLS is supported natively
        if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Native HLS support
          video.src = url;
          video.addEventListener('loadedmetadata', () => {
            video.play().catch(e => console.error('Error playing video:', e));
          });
        } else if (Hls.isSupported()) {
          // Use HLS.js for browsers that don't support HLS natively
          hls = new Hls({ 
            enableWorker: true,
            lowLatencyMode: true,
            backBufferLength: 90
          });
          hls.loadSource(url);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            video.play().catch(e => console.error('Error playing video:', e));
          });
          
          // Error handling
          hls.on(Hls.Events.ERROR, (_, data) => {
            if (data.fatal) {
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  console.error('HLS network error, trying to recover...');
                  hls?.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  console.error('HLS media error, trying to recover...');
                  hls?.recoverMediaError();
                  break;
                default:
                  console.error('HLS fatal error, destroying player:', data);
                  hls?.destroy();
                  break;
              }
            }
          });
        } else {
          console.error('HLS is not supported in this browser');
          setError('当前浏览器不支持HLS视频格式');
        }
      } else if (url.includes('.flv')) {
        // For FLV streams, you would need flv.js
        console.warn('FLV format detected. Please add flv.js support if needed.');
        video.src = url;
      } else {
        // For regular video URLs (mp4, etc.)
        video.src = url;
      }
    };

    // Initialize player when URL changes
    if (url) {
      initPlayer();
    }

    // Cleanup function
    return () => {
      if (hls) {
        hls.destroy();
      }
      if (video) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };
  }, [url]);

  return (
    <div className="video-player-wrapper" style={{ width, height }}>
      <div className="video-player-controls">
        <Space>
          {availableChannels.length > 1 && (
            <Select
              value={selectedChannel?.channelNo}
              onChange={(value) => {
                const channel = availableChannels.find(c => c.channelNo === value);
                if (channel) handleChannelChange(channel);
              }}
              style={{ width: 120 }}
              placeholder="选择通道"
              disabled={loading}
            >
              {availableChannels.map(channel => (
                <Select.Option key={channel.channelNo} value={channel.channelNo}>
                  {channel.channelName || `通道 ${channel.channelNo}`}
                </Select.Option>
              ))}
            </Select>
          )}
          
          <Select
            value={selectedQuality}
            onChange={handleQualityChange}
            style={{ width: 80 }}
            disabled={loading}
          >
            {STREAM_QUALITIES.map(quality => (
              <Select.Option key={quality.value} value={quality.value}>
                {quality.label}
              </Select.Option>
            ))}
          </Select>
          
          <Button 
            icon={<ReloadOutlined />} 
            onClick={handleRefresh}
            loading={loading}
            type="text"
          />
        </Space>
      </div>
      
      <Spin spinning={loading} tip="加载中...">
        <div className="video-player-container" style={{ width: '100%', height: 'calc(100% - 40px)' }}>
          {error ? (
            <div className="video-error-message">
              <p>{error}</p>
              <Button onClick={handleRefresh} type="primary">
                重试
              </Button>
            </div>
          ) : (
            <video
              ref={videoRef}
              className="video-player"
              controls
              autoPlay
              playsInline
              muted
              style={{ width: '100%', height: '100%' }}
            />
          )}
        </div>
      </Spin>
    </div>
  );
};

export default VideoPlayer; 